import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, useHistory, Redirect } from 'react-router-dom';
import { Subheader } from '@m12s/component-library';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';

import { Downtime } from 'components/Downtime';
import { Alarms } from 'components/Alarms';
import { Utilization } from 'components/Utilization';
import MainLayout from 'components/Layouts/MainLayout';
import { CycleTime } from 'components/CycleTime';
import { CycleCountStatus } from 'components/CycleCountStatus';
import { MachineStatus } from 'components/MachineStatus';
import { OEE } from 'components/OEE';
import { PartsGoal } from 'components/PartsGoal';
import Routes from 'lib/utils/routes';
import { CustomTab } from 'components/CustomTab';
import { getMachine } from 'lib/selectors/getMachine';
import { getEffectiveErpResources } from 'lib/selectors/getEffectiveErpResources';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import {
  getEffectiveVisibleTabs,
  getHomeTab,
  getHomeTabRoute,
  getIsHomeTabContextReady,
} from 'lib/selectors/getTabs';
import { getCustomTabs } from 'lib/selectors/getCustomTab';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getIsSetupModeActive } from 'lib/selectors/getIsSetupModeActive';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getIsWaitingForWork } from 'lib/selectors/getIsWaitingForWork';
import { getIsWaitingForOperator } from 'lib/selectors/getIsWaitingForOperator';
import { getIsUnknownJob } from 'lib/selectors/getIsUnknownJob';
import { getHasCards } from 'lib/selectors/getHasCards';
import { getHasUnmatchedCards } from 'lib/selectors/getHasUnmatchedCards';
import { APM_OPDASH_HOST, LABOR_TICKET_HOST } from 'lib/appConfig';
import {
  actionSetEmbeddedLaborTicket,
  actionOpenLaborTicketModal,
  actionSetApmCardContext,
} from 'lib/actions';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import EmbeddedMMDrawer from 'components/EmbeddedMMDrawer';
import { getApmCardPartsContext } from 'lib/selectors/getApmCardPartsContext';
import { getFAIBannerVisible } from 'lib/selectors/getFAIBannerVisible';
import FAIBanner from 'components/FirstArticleInspection/FAIBanner';
import { SingleSelectJobDrawer } from 'components/SingleSelectJobDrawer';
import { getProgramEvents } from 'lib/selectors/getProgramEvents';
import {
  postSplitProgramEvents,
  postSubmitIntervals,
} from 'lib/api/multiJobSelectRest';
import { throwErrorToast, throwStatusToast } from 'lib/utils/toast';
import useSetPartCountMultiplier from 'lib/hooks/useSetPartCountMultiplier';
import WaitingForWork from './MachineMainWrapper/WaitingForWork';
import WaitingForOperator from './MachineMainWrapper/WaitingForOperator';
import UnknownJob from './MachineMainWrapper/UnknownJob';
import Tab from './Tab';

const parseRestErrorMessage = (err) => {
  const raw = err?.message || 'An error occurred while submitting.';
  try {
    const parsed = JSON.parse(raw);
    return parsed.message || parsed.error || raw;
  } catch {
    return raw;
  }
};

const CONFIRM_WORK_EMBED_ZONE_ID = 'labor-ticket-confirm-work';

const ContentContainer = styled(MainLayout.Container)`
  background-color: #fafbfc;
`;

const MachineContent = ({ tab, openSelectJobDrawer }) => {
  const dispatch = useDispatch();
  const machine = useSelector(getMachine);
  const erpResources = useSelector(getEffectiveErpResources);
  const machineRef = machine?.machineRef;
  const scopeStart = useSelector(getScopeStart);
  const currentOperator = useSelector(getCurrentOperator);
  const currentOperatorErpId = currentOperator?.erpId ?? null;
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const isSetupMode = useSelector(getIsSetupModeActive);
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const isWaitingForWork = useSelector(getIsWaitingForWork);
  const isWaitingForOperator = useSelector(getIsWaitingForOperator);
  const isUnknownJob = useSelector(getIsUnknownJob);
  const hasCards = useSelector(getHasCards);
  const hasUnmatchedCards = useSelector(getHasUnmatchedCards);
  const apmParts = useSelector(getApmCardPartsContext);

  // Which APM idle-fallback (if any) the jobs board should show when there are
  // no cards yet. Suppressed in setup mode — the machine's operational status
  // blips to UNKNOWN_JOB while running the unrecognized setup program.
  const apmIdleFallbackKind = useMemo(() => {
    if (!(isApmERPJobFlow && !hasCards && !hasUnmatchedCards && !isSetupMode)) {
      return null;
    }
    if (isUnknownJob) return 'unknownJob';
    if (!currentWorkOrderOp) {
      if (isWaitingForWork) return 'waitingForWork';
      if (isWaitingForOperator) return 'waitingForOperator';
    }
    return null;
  }, [
    isApmERPJobFlow,
    hasCards,
    hasUnmatchedCards,
    isSetupMode,
    isUnknownJob,
    currentWorkOrderOp,
    isWaitingForWork,
    isWaitingForOperator,
  ]);

  // Debounce the fallback: entering setup briefly trips UNKNOWN_JOB (before the
  // setup session lands and clears it), which flashed "Unknown Job" over the
  // board. Only commit to a fallback once its condition has held ~1.2s; if the
  // board resolves (cards arrive / setup mode) first, the fallback never shows
  // and the (always-mounted) embed handles the transition.
  const [apmFallbackSettled, setApmFallbackSettled] = useState(false);
  useEffect(() => {
    // Re-arm the settle timer on EVERY change of the fallback kind — including
    // one non-null kind changing to another (e.g. unknownJob -> waitingForWork).
    // Resetting only when it goes null would let a stale `true` carry over and
    // render the new fallback immediately, skipping the 1.2s debounce.
    setApmFallbackSettled(false);
    if (!apmIdleFallbackKind) {
      return undefined;
    }
    const timer = setTimeout(() => {
      return setApmFallbackSettled(true);
    }, 1200);
    return () => {
      return clearTimeout(timer);
    };
  }, [apmIdleFallbackKind]);

  // Program events are fetched once in DataProvider via useProgramEventDisplayState.
  const programEvents = useSelector(getProgramEvents);

  const [refreshToken, setRefreshToken] = useState(0);
  // Mirror of refreshToken so a save settling outside render (in a promise
  // callback) can derive the next token without a state-updater side effect.
  const refreshTokenRef = useRef(0);
  refreshTokenRef.current = refreshToken;
  const [dismissedItemIds, setDismissedItemIds] = useState([]);
  const [optimisticSubmittedRefs, setOptimisticSubmittedRefs] = useState(() => {
    return new Set();
  });
  const optimisticSubmittedRefsList = useMemo(() => {
    return [...optimisticSubmittedRefs].sort((a, b) => {
      return a - b;
    });
  }, [optimisticSubmittedRefs]);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.dismissItemId;
      if (id) {
        setDismissedItemIds((prev) => {
          return [...prev, id];
        });
      }
      setRefreshToken((t) => {
        return t + 1;
      });
    };
    window.addEventListener('apm:opdash-refresh', handler);
    return () => {
      window.removeEventListener('apm:opdash-refresh', handler);
    };
  }, []);

  // Run Details flyup state
  const [runDetailsOpen, setRunDetailsOpen] = useState(false);
  const [runDetailsContext, setRunDetailsContext] = useState(null);
  // The embedded run-details view drives the drawer title (e.g. "Split
  // production run" while its split flow is active). The default must match the
  // embed's own initial title ("Production run overview") so the header doesn't
  // visibly flip from a host placeholder to the embed's title as the iframe
  // mounts and posts its drawer-title message.
  const [runDetailsTitle, setRunDetailsTitle] = useState(
    'Production run overview'
  );
  // Outcome of the last PCM save ({ token, ok }), passed to the run-details MFE
  // so it can expire its optimistic value on a FAILED save (see the
  // pcm-save-requested handler and the MFE's resolveOptimisticPcm).
  const [pcmSaveOutcome, setPcmSaveOutcome] = useState(null);

  // Confirm Work Produced drawer state
  const [confirmWorkOpen, setConfirmWorkOpen] = useState(false);
  const [confirmWorkContext, setConfirmWorkContext] = useState(null);
  const pendingSubmitRefsRef = useRef(new Set());

  // Owns the PCM save for run-details drawer edits. Hoisted here (always
  // mounted) so closing the drawer can't cancel the in-flight POST / lose the
  // success toast — which happened when the save ran inside the drawer.
  const setPartCountMultiplier = useSetPartCountMultiplier();

  // Refresh the host's grouped-events query now and once more shortly after:
  // a refetch that races an already in-flight poll gets deduplicated onto the
  // stale request, which would leave pre-mutation data up for a full poll.
  const refreshOpdashNow = useCallback(() => {
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
    }, 1500);
  }, []);

  // Closes the run-details drawer and clears the card context so a subsequent
  // labor ticket opened from a different path doesn't inherit stale parts data.
  const closeRunDetails = useCallback(() => {
    setRunDetailsOpen(false);
    setRunDetailsContext(null);
    setRunDetailsTitle('Production run overview');
    dispatch(actionSetApmCardContext(null));
  }, [dispatch]);

  // select-different-job: user picks a different WOO (from NO_MATCH or SUGGESTED)
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:select-different-job',
    useCallback(
      (data) => {
        openSelectJobDrawer(
          data?.eventId
            ? {
                eventId: data.eventId,
                isChangeJob: true,
                sourceEventRefs: data.sourceEventRefs,
              }
            : null
        );
      },
      [openSelectJobDrawer]
    )
  );

  // Grid confirm modal → refresh host program events
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:confirmed',
    useCallback(() => {
      setRefreshToken((t) => {
        return t + 1;
      });
      window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
    }, [])
  );

  // Listen for CONFIRMED card tap → open run details flyup
  useEmbeddedEffect(
    'apm-opdash-display',
    'apm-opdash:view-run-details',
    useCallback(
      (data) => {
        if (
          !currentOperatorErpId &&
          (data?.displayState === 'MATCHED' ||
            data?.displayState === 'CONFIRMED')
        ) {
          throwErrorToast('Sign in to submit labor');
          return;
        }

        dispatch(actionSetApmCardContext(data));
        setRunDetailsContext(data);
        setRunDetailsOpen(true);
      },
      [dispatch, currentOperatorErpId]
    )
  );

  // Listen for close from run details MFE
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:close',
    useCallback(() => {
      closeRunDetails();
    }, [closeRunDetails])
  );

  // Listen for confirmed from run details → close and refresh opdash cards
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:confirmed',
    useCallback(() => {
      closeRunDetails();
      setRefreshToken((t) => {
        return t + 1;
      });
    }, [closeRunDetails])
  );

  // PCM save requested from the run-details drawer. The drawer flips its own
  // card optimistically and delegates the actual write to the host, which owns
  // the POST + toast + feed refresh via the shared hook — so closing the drawer
  // can't cancel the request. The drawer knows only the run's WOO, so we pass it
  // as an override (see useSetPartCountMultiplier).
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:pcm-save-requested',
    useCallback(
      (data) => {
        if (!data?.workOrderOperationRef || data?.partCountMultiplier == null) {
          throwErrorToast('Failed to update part count');
          return;
        }
        // The hook owns its own toast + optimistic rollback and only re-throws
        // unexpected non-Apollo failures. It resolves true on success / false on
        // a handled failure. When the save SETTLES we bump the refresh token AND
        // record the outcome against that token, so the run-details MFE can
        // expire its optimistic PCM value on a FAILED save (where the feed never
        // moves and would otherwise leave the card stuck). Success is left to the
        // MFE's feed-driven release so it doesn't flicker to the pre-refetch value.
        const settle = (ok) => {
          const nextToken = refreshTokenRef.current + 1;
          setRefreshToken(nextToken);
          setPcmSaveOutcome({ token: nextToken, ok });
        };
        setPartCountMultiplier(data.partCountMultiplier, data.actualParts, {
          workOrderOperationRef: data.workOrderOperationRef,
        })
          .then((ok) => {
            return settle(ok !== false);
          })
          .catch(() => {
            return settle(false);
          });
      },
      [setPartCountMultiplier]
    )
  );

  // Listen for voided from run details → close and refresh opdash cards
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:voided',
    useCallback(() => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      setRefreshToken((t) => {
        return t + 1;
      });
      refreshOpdashNow();
    }, [refreshOpdashNow])
  );

  // Partial interval void — refresh cards without closing run details
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:intervals-voided',
    useCallback(() => {
      setRefreshToken((t) => {
        return t + 1;
      });
      refreshOpdashNow();
    }, [refreshOpdashNow])
  );

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:void-failed',
    useCallback((data) => {
      throwErrorToast(data?.message || 'Failed to delete intervals');
    }, [])
  );

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:ignore-failed',
    useCallback((data) => {
      throwErrorToast(data?.message || 'Failed to ignore program');
    }, [])
  );

  // Server caught up for optimistically submitted intervals — drop local overrides
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:submitted-confirmed',
    useCallback((data) => {
      const refs = data?.programEventRefs ?? [];
      if (refs.length === 0) return;
      setOptimisticSubmittedRefs((prev) => {
        const next = new Set(prev);
        refs.forEach((ref) => {
          next.delete(ref);
        });
        return next.size === prev.size ? prev : next;
      });
    }, [])
  );

  // Listen for ignored from run details → dismiss card immediately and refetch grouped
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:ignored',
    useCallback((data) => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      window.dispatchEvent(
        new CustomEvent('apm:opdash-refresh', {
          detail: { dismissItemId: data?.dismissItemId },
        })
      );
    }, [])
  );

  // Pull job → close details, dismiss the card immediately, refetch grouped
  // (the card stays gone durably via the grouping endpoint's pulled filter).
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:pull-job',
    useCallback((data) => {
      setRunDetailsOpen(false);
      setRunDetailsContext(null);
      window.dispatchEvent(
        new CustomEvent('apm:opdash-refresh', {
          detail: { dismissItemId: data?.dismissItemId },
        })
      );
    }, [])
  );

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:pull-failed',
    useCallback((data) => {
      throwErrorToast(data?.message || 'Failed to pull job');
    }, [])
  );

  // Listen for change-job from run details → close details, open select-job drawer
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:change-job',
    useCallback(
      (data) => {
        closeRunDetails();
        openSelectJobDrawer(
          data?.eventId
            ? {
                eventId: data.eventId,
                isChangeJob: true,
                sourceEventRefs: data.sourceEventRefs,
              }
            : null
        );
      },
      [closeRunDetails, openSelectJobDrawer]
    )
  );

  // Listen for labor ticket click from run details → close details, open LT form
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:open-ticket',
    useCallback(
      (data) => {
        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }

        if (data?.laborTicketRef) {
          setRunDetailsOpen(false);
          setRunDetailsContext(null);
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: currentOperatorErpId,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
        }
      },
      [dispatch, currentOperatorErpId]
    )
  );

  // The run-details embed retitles the drawer when it swaps between its
  // run-details and split views.
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:drawer-title',
    useCallback((data) => {
      setRunDetailsTitle(data?.title || 'Production run overview');
    }, [])
  );

  // Split-change-job: open select-job drawer with split context. The picker
  // does NOT execute the split — picking a job reopens run details at the
  // split review step (see apm:split-job-selected below), and only the
  // review's Confirm submits.
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-change-job',
    useCallback(
      (data) => {
        const resumeRunDetailsContext = runDetailsContext;
        closeRunDetails();
        openSelectJobDrawer(
          { isSplitChangeJob: true },
          { ...data, resumeRunDetailsContext }
        );
      },
      [closeRunDetails, openSelectJobDrawer, runDetailsContext]
    )
  );

  // A destination job was picked during split-change-job — reopen the
  // run-details drawer directly on the split review step with the draft.
  useEffect(() => {
    const handler = (event) => {
      const { splitContext, pickedWorkOrderOp } = event.detail || {};
      if (!splitContext || !pickedWorkOrderOp) return;
      setRunDetailsContext({
        ...(splitContext.resumeRunDetailsContext || {}),
        splitDraft: {
          splitTime: splitContext.splitTime,
          workOrderOperationRef: splitContext.workOrderOperationRef,
          newWorkOrderOperation: pickedWorkOrderOp,
        },
      });
      setRunDetailsTitle('Split production run');
      setRunDetailsOpen(true);
    };
    window.addEventListener('apm:split-job-selected', handler);
    return () => {
      window.removeEventListener('apm:split-job-selected', handler);
    };
  }, []);

  // The labor-ticket form's Split action (via ERPMultiJobControlBar, which
  // closed the submit-all drawer) hands off here — open the run-details drawer
  // straight into split mode for that run. The embed resolves the run from the
  // host's programEvents + workOrderOperationRef and enters the split view.
  useEffect(() => {
    const handler = (event) => {
      const { workOrderOperationRef } = event.detail || {};
      if (workOrderOperationRef == null) return;
      setRunDetailsContext({ workOrderOperationRef, startInSplit: true });
      setRunDetailsTitle('Split production run');
      setRunDetailsOpen(true);
    };
    window.addEventListener('apm:open-run-details-split', handler);
    return () => {
      window.removeEventListener('apm:open-run-details-split', handler);
    };
  }, []);

  // The embed now performs the split POST itself so its review screen can
  // hold a busy Confirm until the server accepts. split-complete arrives only
  // after success — close the drawer and refresh. split-failed toasts while
  // the embed keeps the review open for retry.
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-complete',
    useCallback(() => {
      closeRunDetails();
      setRefreshToken((t) => {
        return t + 1;
      });
      // Refresh the host's grouped-events query too — without this the jobs
      // page waits out the poll interval before showing the split's result.
      refreshOpdashNow();
    }, [closeRunDetails, refreshOpdashNow])
  );

  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-failed',
    useCallback((data) => {
      throwErrorToast(data?.message || 'Failed to split production run');
    }, [])
  );

  // Transitional: older embed bundles hand the POST to the host. Remove once
  // apm-opdash-display releases with embed-owned split.
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:split-submit',
    useCallback(
      async (data) => {
        setRunDetailsOpen(false);
        setRunDetailsContext(null);
        try {
          await postSplitProgramEvents(
            data.machineRef,
            data.splitTime,
            data.workOrderOperationRef,
            data.newWorkOrderOperationRef ?? null
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('split-submit failed', err);
          throwErrorToast('Failed to split production run');
        }
        setRefreshToken((t) => {
          return t + 1;
        });
        refreshOpdashNow();
      },
      [refreshOpdashNow]
    )
  );

  // Listen for confirm-work-produced from run details → close details, open confirm form
  useEmbeddedEffect(
    'apm-opdash-run-details',
    'apm-opdash-run-details:confirm-work-produced',
    useCallback(
      (data) => {
        setRunDetailsOpen(false);
        setRunDetailsContext(null);

        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }

        if (data?.setupMode && data?.laborTicketRef) {
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: currentOperatorErpId,
              workOrderId: data?.jobNumber,
              sequenceNumber: data?.sequenceNumber,
              splitTime: data.splitTime,
            })
          );
          dispatch(actionOpenLaborTicketModal('submit'));
          return;
        }

        setConfirmWorkContext({
          machineRef: data?.machineRef ?? machineRef,
          workOrderOperationRef: data?.workOrderOperationRef,
          jobNumber: data?.jobNumber,
          partNumber: data?.partNumber,
          sequenceNumber: data?.sequenceNumber,
          finishQuantity: data?.finishQuantity ?? null,
          // Historical Job-total baseline (backend orderQuantity) so the form's
          // right-hand "Job total" panel shows ALL submitted parts for the WOO,
          // not just the current submission.
          submittedGoodParts: data?.submittedGoodParts ?? null,
          submittedBadParts: data?.submittedBadParts ?? null,
          personId: currentOperatorErpId,
          intervals: data?.intervals ?? [],
          programEventRefs: data?.programEventRefs ?? [],
          wooCustomFieldsRaw: data?.wooCustomFieldsRaw ?? null,
          isSubmittingAllForJob: data?.isSubmittingAllForJob ?? false,
        });
        setConfirmWorkOpen(true);
      },
      [machineRef, currentOperatorErpId, dispatch]
    )
  );

  const handleConfirmWorkDismiss = useCallback(() => {
    setConfirmWorkOpen(false);
    setConfirmWorkContext(null);
  }, []);

  // Split from the standalone confirm-work drawer. (The submit-all drawer's
  // Split is handled in ERPMultiJobControlBar on its own zone.) Close the
  // confirm-work form and open run-details straight into split mode for this run.
  useEmbeddedEffect(
    CONFIRM_WORK_EMBED_ZONE_ID,
    'labor-ticket-form:split-job',
    useCallback(
      (data) => {
        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }
        if (data?.workOrderOperationRef == null) {
          return;
        }
        setConfirmWorkOpen(false);
        setRunDetailsContext({
          workOrderOperationRef: data.workOrderOperationRef,
          startInSplit: true,
        });
        setRunDetailsTitle('Split production run');
        setRunDetailsOpen(true);
      },
      [currentOperatorErpId]
    )
  );

  useEmbeddedEffect(
    CONFIRM_WORK_EMBED_ZONE_ID,
    'labor-ticket-form:submit-intervals-requested',
    useCallback(
      async (data) => {
        if (!currentOperatorErpId) {
          throwErrorToast('Sign in to submit labor');
          return;
        }

        const programEventRefs =
          data?.programEventRefs ??
          (data?.intervals ?? []).map((interval) => {
            return interval.programEventRef;
          });
        const refsToSubmit = programEventRefs.filter((ref) => {
          return !pendingSubmitRefsRef.current.has(ref);
        });
        if (refsToSubmit.length === 0) {
          return;
        }
        refsToSubmit.forEach((ref) => {
          pendingSubmitRefsRef.current.add(ref);
        });

        const submitMachineRef = data?.machineRef ?? machineRef;
        const {
          machineRef: _machineRef,
          programEventRefs: _refs,
          ...apiPayload
        } = data;

        setConfirmWorkOpen(false);
        setConfirmWorkContext(null);
        setOptimisticSubmittedRefs((prev) => {
          return new Set([...prev, ...refsToSubmit]);
        });
        setRefreshToken((t) => {
          return t + 1;
        });
        window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        throwStatusToast('Submitting…', 'info', { autoClose: 2000 });

        try {
          await postSubmitIntervals(submitMachineRef, apiPayload);
          setRefreshToken((t) => {
            return t + 1;
          });
          window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        } catch (err) {
          setOptimisticSubmittedRefs((prev) => {
            const next = new Set(prev);
            refsToSubmit.forEach((ref) => {
              next.delete(ref);
            });
            return next;
          });
          throwErrorToast(parseRestErrorMessage(err));
        } finally {
          refsToSubmit.forEach((ref) => {
            pendingSubmitRefsRef.current.delete(ref);
          });
        }
      },
      [machineRef, currentOperatorErpId]
    )
  );

  const tabIdentifier = tab.id;
  switch (tabIdentifier) {
    case 'cycleCount':
      return <CycleCountStatus />;
    case 'jobs': {
      // Compute the APM "no cards yet" fallback (Unknown Job / Waiting for
      // Work / Waiting for Operator), but do NOT return it in place of the
      // embed. Swapping it in unmounts the apm-opdash iframe and forces a ~1s
      // reload every time the board momentarily empties (e.g. while entering
      // setup, before the grouped refetch lands). Instead keep the embed
      // mounted and hide it (display:none) behind the fallback — the iframe
      // stays warm, so transitions are instant and there's no flicker.
      // Render the idle fallback only once it has settled (see the debounce
      // above) — a transient UNKNOWN_JOB during setup entry never flashes. The
      // embed stays mounted and hidden behind it, so there's no iframe reload.
      let apmFallbackView = null;
      if (apmIdleFallbackKind && apmFallbackSettled) {
        if (apmIdleFallbackKind === 'unknownJob') {
          apmFallbackView = <UnknownJob />;
        } else if (apmIdleFallbackKind === 'waitingForWork') {
          apmFallbackView = <WaitingForWork />;
        } else if (apmIdleFallbackKind === 'waitingForOperator') {
          apmFallbackView = <WaitingForOperator />;
        }
      }
      return (
        <>
          {apmFallbackView}
          <div
            style={{
              position: 'relative',
              display: apmFallbackView ? 'none' : 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            <MMEmbeddableZoneWithLoader
              id="apm-opdash-display"
              src={APM_OPDASH_HOST}
              path="/"
              context={{
                machineRef: machine?.machineRef,
                machineId: machine?.id,
                start: scopeStart,
                erpResources,
                refreshToken,
                dismissedItemIds,
                personId: currentOperatorErpId,
                programEvents,
                setupMode: isSetupMode,
              }}
            />
          </div>
          {/* Drawers live OUTSIDE the embed container: the container is
              display:none while an idle fallback shows, and a drawer opened
              (e.g. run-details, or confirm-work) must stay visible even then. */}
          <MMDrawer
            isOpen={runDetailsOpen}
            onClose={closeRunDetails}
            title={runDetailsTitle}
          >
            <MMEmbeddableZoneWithLoader
              id="apm-opdash-run-details"
              src={APM_OPDASH_HOST}
              path="/run-details"
              context={{
                start: scopeStart,
                personId: currentOperatorErpId,
                erpResources,
                programEvents,
                refreshToken,
                pcmSaveOutcome,
                optimisticSubmittedRefs: optimisticSubmittedRefsList,
                ...runDetailsContext,
                machineRef: machine?.machineRef,
                partsContext: apmParts ? { apmParts } : undefined,
              }}
            />
          </MMDrawer>
          <EmbeddedMMDrawer
            embedZoneId={CONFIRM_WORK_EMBED_ZONE_ID}
            messageSource="labor-ticket-form"
            src={LABOR_TICKET_HOST}
            path="/confirm-work-produced"
            context={confirmWorkContext}
            isOpen={confirmWorkOpen}
            onDismiss={handleConfirmWorkDismiss}
            defaultTitle="Confirm work produced"
          />
        </>
      );
    }
    case 'partCount':
      return <MachineStatus />;
    case 'utilization':
      return <Utilization />;
    case 'downtime':
      return <Downtime />;
    case 'alarms':
      return <Alarms />;
    case 'partsGoal':
      return <PartsGoal />;
    case 'cycleTime':
      return <CycleTime />;
    case 'oee':
      return <OEE />;
    default:
      return null;
  }
};

const MachineMain = ({ tab: currentTab }) => {
  const history = useHistory();
  const machine = useSelector(getMachine);
  const tabs = useSelector(getEffectiveVisibleTabs);
  const scopeStart = useSelector(getScopeStart);
  const homeTabRoute = useSelector(getHomeTabRoute);
  const homeTab = useSelector(getHomeTab);
  const isHomeTabContextReady = useSelector(getIsHomeTabContextReady);
  const customTabs = useSelector(getCustomTabs);
  const faiBannerVisible = useSelector(getFAIBannerVisible);
  const isApmERPJobFlow = useSelector(getIsApmERPJobFlow);
  const currentOperator = useSelector(getCurrentOperator);
  const windowPath = window.location.pathname;
  const hasAppliedApmHomeTab = useRef(false);

  // --- Single-select job drawer state (lives here so it survives tab switches) ---
  const [selectJobDrawerOpen, setSelectJobDrawerOpen] = useState(false);
  const [drawerApmFlow, setDrawerApmFlow] = useState(null);
  const [drawerSplitContext, setDrawerSplitContext] = useState(null);

  const openSelectJobDrawer = useCallback((navState, splitPayload) => {
    setDrawerApmFlow(navState || null);
    setDrawerSplitContext(splitPayload || null);
    setSelectJobDrawerOpen(true);
  }, []);

  const completeSelectJobDrawer = useCallback(() => {
    setSelectJobDrawerOpen(false);
    setDrawerApmFlow(null);
    setDrawerSplitContext(null);
    window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
  }, []);

  // Split-change-job: the picker only CHOOSES the destination. Close the
  // drawer and hand the pick back to the run-details flyup, which reopens on
  // the split review step — nothing is submitted until the operator confirms.
  const handleSplitJobSelected = useCallback(
    (pickedWorkOrderOp) => {
      const splitContext = drawerSplitContext;
      setSelectJobDrawerOpen(false);
      setDrawerApmFlow(null);
      setDrawerSplitContext(null);
      if (!splitContext) return;
      window.dispatchEvent(
        new CustomEvent('apm:split-job-selected', {
          detail: { splitContext, pickedWorkOrderOp },
        })
      );
    },
    [drawerSplitContext]
  );

  const selectJobFlowContextValue = useMemo(() => {
    return {
      apmFlowState: drawerApmFlow,
      splitJobContext: drawerSplitContext,
      onApmFlowComplete: completeSelectJobDrawer,
      onSplitJobSelected: handleSplitJobSelected,
    };
  }, [
    drawerApmFlow,
    drawerSplitContext,
    completeSelectJobDrawer,
    handleSplitJobSelected,
  ]);

  useEffect(() => {
    if (isApmERPJobFlow && !currentOperator && selectJobDrawerOpen) {
      completeSelectJobDrawer();
    }
  }, [
    isApmERPJobFlow,
    currentOperator,
    selectJobDrawerOpen,
    completeSelectJobDrawer,
  ]);

  useEffect(() => {
    const handler = () => {
      if (isApmERPJobFlow && !currentOperator) {
        return;
      }
      openSelectJobDrawer(null);
    };
    window.addEventListener('apm:open-select-job-drawer', handler);
    return () => {
      window.removeEventListener('apm:open-select-job-drawer', handler);
    };
  }, [openSelectJobDrawer, isApmERPJobFlow, currentOperator]);

  useEffect(() => {
    if (
      hasAppliedApmHomeTab.current ||
      !isHomeTabContextReady ||
      !isApmERPJobFlow ||
      !homeTab ||
      currentTab === homeTab
    ) {
      return;
    }

    hasAppliedApmHomeTab.current = true;
    history.replace(Routes.machineIdHomeTabPath(machine.id, homeTab));
  }, [
    isHomeTabContextReady,
    isApmERPJobFlow,
    homeTab,
    currentTab,
    machine.id,
    history,
  ]);

  if (!scopeStart || !homeTabRoute) {
    return null;
  }

  if (faiBannerVisible) {
    return <FAIBanner />;
  }

  return (
    <>
      <Subheader
        defaultValue={currentTab}
        onChange={(value) => {
          return history.push(Routes.machineIdHomeTabPath(machine.id, value));
        }}
      >
        {tabs.length > 1 &&
          tabs.map((tab) => {
            return <Tab key={tab.id} tab={tab} />;
          })}
      </Subheader>
      <ContentContainer>
        {customTabs.map((customTab) => {
          return (
            <CustomTab
              key={customTab.id}
              tab={customTab}
              base={Routes.machineIdHomeTabPath(machine.id, customTab.id)}
              hidden={
                windowPath !==
                Routes.machineIdHomeTabPath(machine.id, customTab.id)
              }
            />
          );
        })}
        <Switch>
          {tabs.map((tab) => {
            return (
              <Route
                key={tab.id}
                path={Routes.machineIdHomeTabPath(machine.id, tab.id)}
              >
                <MachineContent
                  tab={tab}
                  openSelectJobDrawer={openSelectJobDrawer}
                />
              </Route>
            );
          })}
          <Route path={Routes.machineIdHomePath(machine.id)}>
            <Redirect to={homeTabRoute} />
          </Route>
        </Switch>
      </ContentContainer>
      {isApmERPJobFlow && (
        <SingleSelectJobDrawer
          isOpen={selectJobDrawerOpen}
          onClose={completeSelectJobDrawer}
          flowContextValue={selectJobFlowContextValue}
        />
      )}
    </>
  );
};

MachineMain.propTypes = {
  tab: PropTypes.string.isRequired,
};

export default MachineMain;
